import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import * as userApi from '../../lib/api/user';

const DOC_TYPE_OPTIONS = [
  { value: '', label: '전체 문서' },
  { value: 'ORDER_BOOK', label: '수주품의서' },
  { value: 'HR_LEAVE', label: '휴가' },
];

const REASON_OPTIONS = [
  { value: 'MANUAL', label: '수동 지정' },
  { value: 'LEAVE', label: '휴가' },
  { value: 'BUSINESS_TRIP', label: '출장' },
  { value: 'ABSENCE', label: '부재' },
];

const emptyForm = {
  delegateUsrUuid: '',
  delegateUsrNm: '',
  docType: '',
  strtDt: dayjs().format('YYYY-MM-DD'),
  endDt: dayjs().add(7, 'day').format('YYYY-MM-DD'),
  reasonTyp: 'MANUAL',
};

// 대결(위임) 생성/수정 다이얼로그. ksm_frontend ApprovalDelegationModal.js 포팅(2026-10-20).
const ApprovalDelegationModal = ({ open, delegation, currentUserUuid, onClose, onSave }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    userApi.getUsers().then((res) => setUsers(res?.data?.list || []));
  }, []);

  useEffect(() => {
    if (delegation) {
      setForm({
        delegateUsrUuid: delegation.delegateUsrUuid,
        delegateUsrNm: delegation.delegateUsrNm,
        docType: delegation.docType || '',
        strtDt: delegation.strtDt,
        endDt: delegation.endDt,
        reasonTyp: delegation.reasonTyp || 'MANUAL',
      });
    } else {
      setForm(emptyForm);
    }
  }, [delegation, open]);

  const handleSave = () => {
    onSave({
      delegateUsrUuid: form.delegateUsrUuid,
      docType: form.docType || null,
      strtDt: form.strtDt,
      endDt: form.endDt,
      reasonTyp: form.reasonTyp,
    });
  };

  const selectedDelegate = users.find((u) => u.usrUuid === form.delegateUsrUuid) || null;
  const canSave = form.delegateUsrUuid && form.strtDt && form.endDt && form.strtDt <= form.endDt;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{delegation ? '대결 설정 수정' : '대결 설정 추가'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={users.filter((u) => u.usrUuid !== currentUserUuid)}
            getOptionLabel={(option) => option?.usrNm || ''}
            isOptionEqualToValue={(option, value) => option.usrUuid === value?.usrUuid}
            value={selectedDelegate}
            onChange={(e, value) =>
              setForm({
                ...form,
                delegateUsrUuid: value ? value.usrUuid : '',
                delegateUsrNm: value ? value.usrNm : '',
              })
            }
            renderInput={(params) => <TextField {...params} label="대결자" required />}
          />
          <TextField
            select
            label="대상 문서"
            value={form.docType}
            onChange={(e) => setForm({ ...form, docType: e.target.value })}
            fullWidth
          >
            {DOC_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          {/*
            [이슈 #10, 2026-10-21] 네이티브 <input type="date">는 년/월/일 세그먼트를 브라우저가
            직접 관리해서, 빠른 연속 입력(자동화 도구 등)에 포커스 전환을 놓치면
            "202610.02.00" 식으로 깨진 값이 들어갈 수 있었다(달력 클릭은 항상 정상). 이
            코드베이스가 다른 화면(BillPlanEditDialog 등)에서 이미 쓰는 MUI X DatePicker로
            바꿔 같은 방식으로 통일한다.
          */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={2}>
              <DatePicker
                label="시작일"
                format="YYYY-MM-DD"
                value={form.strtDt ? dayjs(form.strtDt) : null}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    setForm({ ...form, strtDt: newValue.format('YYYY-MM-DD') });
                  }
                }}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="종료일"
                format="YYYY-MM-DD"
                value={form.endDt ? dayjs(form.endDt) : null}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    setForm({ ...form, endDt: newValue.format('YYYY-MM-DD') });
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: form.strtDt > form.endDt,
                    helperText: form.strtDt > form.endDt ? '종료일이 시작일보다 빠릅니다.' : '',
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>
          <TextField
            select
            label="사유"
            value={form.reasonTyp}
            onChange={(e) => setForm({ ...form, reasonTyp: e.target.value })}
            fullWidth
          >
            {REASON_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          취소
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!canSave}>
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalDelegationModal;
